import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { RefreshCw, ArrowRight, Scale } from 'lucide-react';

import { parseUtc } from '../../lib/parseUtc';
/**
 * RebalancePreviewPanel — the CAST-style Rebalancer:
 *
 *   Current configuration        Rebalanced configuration
 *   Q× type  CPU GiB $/h /mo  →  Q× type  CPU GiB $/h /mo
 *   INITIAL COMPUTE COST          PREDICTED OPTIMIZED COST
 *   [ nodes to replace · predicted to save % · cost bars · Rebalance ]
 *   … and while a plan executes: progress (creating n/n · cleaning n/n) + log.
 *
 * Data: GET /node-optimization/rebalance-configuration — fleet grouped by
 * (instance type, capacity) priced from the SAME Infracost-backed catalog the
 * planner uses (no client-side pricing). Apply: the existing
 * /rebalance-plan/apply rail (agent → RebalanceClaim → engine).
 */
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const PHASE_LABELS = {
  InProgress: 'In progress',
  Pending: 'Pending',
  Completed: 'Completed',
  Failed: 'Failed',
  Partial: 'Partially completed',
  Cancelled: 'Cancelled',
};
const phaseLabel = (p) => PHASE_LABELS[p] || String(p || '').replace(/[-_]/g, ' ');
const phasePillClass = (p) =>
  p === 'Completed'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
    : p === 'Failed'
      ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
      : p === 'Partial'
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400';

const CapBadge = ({ cap }) => (
  <span
    className={[
      'ml-1.5 rounded px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide',
      cap === 'spot'
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
    ].join(' ')}
  >
    {cap}
  </span>
);

const ConfigTable = ({ title, rows, totalLabel, totalMonthly, accent }) => {
  const inst = rows.reduce((a, r) => a + r.qty, 0);
  const cpu = rows.reduce((a, r) => a + (r.cpu || 0), 0);
  const gib = rows.reduce((a, r) => a + (r.gib || 0), 0);
  return (
    <div className="flex-1 min-w-[300px]">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wide text-slate-400 font-medium">Qty</th>
              <th className="px-3 py-2 text-left text-[11px] uppercase tracking-wide text-slate-400 font-medium">Name</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide text-slate-400 font-medium">CPU</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide text-slate-400 font-medium">GiB</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide text-slate-400 font-medium">$/h</th>
              <th className="px-3 py-2 text-right text-[11px] uppercase tracking-wide text-slate-400 font-medium">Total/mo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-4 text-center text-slate-400">none</td></tr>
            ) : rows.map((r, i) => (
              <tr key={i} className="text-slate-700 dark:text-slate-300">
                <td className="px-3 py-2 tabular-nums">{r.qty}×</td>
                <td className="px-3 py-2 font-mono">{r.instance_type}<CapBadge cap={r.capacity_type} /></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.cpu}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.gib}</td>
                <td className="px-3 py-2 text-right tabular-nums">${Number(r.hourly_unit || 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(r.monthly_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={`flex items-center justify-between border-t border-slate-200 dark:border-slate-800 px-3 py-2 ${accent ? 'bg-emerald-50/60 dark:bg-emerald-950/30' : 'bg-slate-50 dark:bg-slate-900'}`}>
          <span className="text-[11px] text-slate-500">
            {totalLabel} · {inst} instances · {cpu} CPU · {Math.round(gib)} GiB
          </span>
          <span className={`text-sm font-semibold tabular-nums ${accent ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-900 dark:text-slate-100'}`}>
            {money(totalMonthly)}<span className="text-[11px] font-normal text-slate-400">/mo</span>
          </span>
        </div>
      </div>
    </div>
  );
};

const RebalancePreviewPanel = ({ clusterId }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    if (!clusterId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-configuration`);
      setCfg(r.data || null);
    } catch {
      setCfg(null);
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // live progress while plans execute
    return () => clearInterval(t);
  }, [load]);

  const applyRebalance = async () => {
    setApplying(true); setMsg(null);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/rebalance-plan/apply`);
      setMsg('Rebalance queued — changes will start within about a minute.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || e.message);
    } finally {
      setApplying(false);
    }
  };

  if (!cfg || (!cfg.current?.length && !cfg.available)) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-6 py-8 text-center text-sm text-slate-500">
        {loading ? 'Loading fleet configuration…' : 'No nodes reported for this cluster. Rebalance previews appear once the agent connects.'}
      </div>
    );
  }

  const executing = cfg.phase && !['Completed', 'Failed', 'Partial', 'Cancelled'].includes(cfg.phase);
  const prog = cfg.progress || {};
  const creating = prog.creating_total ? Math.round((100 * prog.creating_done) / prog.creating_total) : 0;
  const cleaning = prog.cleaning_total ? Math.round((100 * prog.cleaning_done) / prog.cleaning_total) : 0;
  const overall = prog.creating_total + prog.cleaning_total
    ? Math.round((100 * (prog.creating_done + prog.cleaning_done)) / (prog.creating_total + prog.cleaning_total))
    : 0;
  const replaceTotal = cfg.progress?.cleaning_total || 0;
  const barMax = Math.max(cfg.current_monthly || 0, cfg.target_monthly || 0, 1);

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center justify-between px-5 pt-4">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Rebalancer</h3>
          {cfg.phase && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${phasePillClass(cfg.phase)}`}
              title={cfg.plan_name || undefined}
            >
              {phaseLabel(cfg.phase)}
            </span>
          )}
        </div>
        <button onClick={load} className="text-slate-400 hover:text-slate-600" title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── in-progress view (CAST "Rebalance in progress") ── */}
      {executing && (
        <div className="mx-5 mt-4 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/50 dark:bg-emerald-950/30 p-4">
          <div className="flex items-baseline justify-between">
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">Rebalance in progress</p>
            <p className="text-2xl font-semibold tabular-nums text-emerald-900 dark:text-emerald-200">{overall}%</p>
          </div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-emerald-100 dark:bg-emerald-900">
            <div className="h-full bg-emerald-600 transition-all" style={{ width: `${overall}%` }} />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-6 text-xs">
            <div>
              <div className="flex justify-between text-slate-500">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Creating nodes</span><span className="tabular-nums">{prog.creating_done}/{prog.creating_total}</span>
              </div>
              <div className="mt-1 h-1 rounded bg-slate-200 dark:bg-slate-800">
                <div className="h-full rounded bg-slate-900 dark:bg-slate-200" style={{ width: `${creating}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-slate-500">
                <span className="text-[10px] uppercase tracking-wide text-slate-400">Removing old nodes</span><span className="tabular-nums">{prog.cleaning_done}/{prog.cleaning_total}</span>
              </div>
              <div className="mt-1 h-1 rounded bg-slate-200 dark:bg-slate-800">
                <div className="h-full rounded bg-slate-900 dark:bg-slate-200" style={{ width: `${cleaning}%` }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── configuration comparison ── */}
      {/* A terminal latest plan is a RESULT already reflected in the current
          fleet — show the fleet alone, never a stale diff (which double-counts
          the executed plan's creates). */}
      {cfg.available ? (
        <div className="flex flex-wrap items-start gap-4 p-5">
          <ConfigTable
            title="Current configuration"
            rows={cfg.current || []}
            totalLabel="Initial compute cost"
            totalMonthly={cfg.current_monthly}
          />
          <div className="hidden lg:flex h-full items-center pt-16">
            <ArrowRight className="h-5 w-5 text-slate-300" />
          </div>
          <ConfigTable
            title="Rebalanced configuration"
            rows={cfg.target || []}
            totalLabel="Predicted optimized cost"
            totalMonthly={cfg.target_monthly}
            accent
          />
        </div>
      ) : (
        <div className="p-5">
          <ConfigTable
            title="Current fleet"
            rows={cfg.current || []}
            totalLabel="Current compute cost"
            totalMonthly={cfg.current_monthly}
          />
          {/* Declare the SCOPE. This table prices only Karpenter-managed nodes,
              while the savings hero prices the whole fleet the agent sees — so
              the two totals legitimately differ and, unlabelled, read as a bug. */}
          <p className="mt-2 text-[11px] text-slate-400">
            Karpenter-managed nodes only — nodes outside a NodePool aren’t priced here.
            Cluster-wide “requested” includes pods pinned to excluded nodes and one
            DaemonSet copy per node; both shrink out of the optimized target, which is
            why it can be smaller than the headline request figure.
          </p>
          <p
            className="mt-3 text-xs text-slate-500"
            title={cfg.plan_name ? `Updated by last rebalance: ${cfg.plan_name} (${cfg.phase})` : undefined}
          >
            Your cluster is already balanced. We'll keep checking for new savings.
          </p>
        </div>
      )}

      {/* ── bottom action bar (CAST review footer) — only for a PENDING plan ── */}
      {cfg.available && (
      <div className="flex flex-wrap items-center gap-6 border-t border-slate-200 dark:border-slate-800 px-5 py-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Nodes to replace</p>
          <p className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{replaceTotal}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">Predicted to save</p>
          <p className="text-sm font-semibold tabular-nums text-emerald-600">{Number(cfg.savings_pct || 0).toFixed(1)}%</p>
        </div>
        <div className="min-w-[220px] flex-1 space-y-1">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="w-24 shrink-0">Current cost</span>
            <div className="h-1.5 flex-1 rounded bg-slate-100 dark:bg-slate-800">
              <div className="h-full rounded bg-slate-500" style={{ width: `${(100 * (cfg.current_monthly || 0)) / barMax}%` }} />
            </div>
            <span className="tabular-nums">{money(cfg.current_monthly)}</span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className="w-24 shrink-0">Rebalanced</span>
            <div className="h-1.5 flex-1 rounded bg-slate-100 dark:bg-slate-800">
              <div className="h-full rounded bg-emerald-500" style={{ width: `${(100 * (cfg.target_monthly || 0)) / barMax}%` }} />
            </div>
            <span className="tabular-nums">{money(cfg.target_monthly)}</span>
          </div>
        </div>
        <button
          onClick={applyRebalance}
          disabled={applying || !cfg.available || executing}
          title={executing ? 'A rebalance is already running' : !cfg.available ? 'No rebalance plan available' : 'Apply this rebalance plan'}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {applying ? 'Queuing…' : 'Apply rebalance'}
        </button>
      </div>
      )}
      {msg && <p className="px-5 pb-3 text-xs text-slate-500">{msg}</p>}

      {/* ── rebalancing log ── */}
      {(cfg.log || []).length > 0 && (
        <div className="border-t border-slate-200 dark:border-slate-800 px-5 py-3">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-400">Rebalancing log</p>
          <div className="max-h-44 overflow-y-auto">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {cfg.log.map((l, i) => (
                  <tr key={i} className="text-slate-600 dark:text-slate-400">
                    <td className="py-1.5 pr-4 whitespace-nowrap tabular-nums">{l.timestamp ? parseUtc(l.timestamp).toLocaleString() : '—'}</td>
                    <td className="py-1.5 pr-4 font-mono">{l.operation}</td>
                    <td className="py-1.5 text-right text-slate-400">{l.initiated_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RebalancePreviewPanel;
